import { inject, Injectable, signal } from '@angular/core';
import { GeoJSONSourceSpecification, LayerSpecification, Map as MapBox } from 'mapbox-gl';
import { Observable } from 'rxjs';
import { AccountStateService } from '../account-state.service';
import { DevicesFilters, DevicesManagerService, FullDevice } from '../devices-manager.service';
import { LocationsService } from '../locations.service';
import { MapService } from '../map.service';
import BaseLayer from './BaseLayer';
import { DevicesLayerService } from './devices-layer.service';
import { EventsDeviceUtilsService } from './events-devices-utils';

@Injectable({
  providedIn: 'root',
})
export class ClustersDevicesLayer extends BaseLayer<FullDevice> {
  public $visiblePoints = signal(null);

  public override mapService = inject<Omit<MapService, 'map'> & { map: MapBox }>(MapService);
  public locationsService = inject(LocationsService);
  public devicesLayerService = inject(DevicesLayerService);
  public _accountStateService = inject(AccountStateService);

  readonly _CLUSTERS_FILLS_LAYER = this.mapService._CLUSTERS_FILLS_LAYER;
  readonly _CLUSTERS_COUNT_LAYER = this.mapService._CLUSTERS_COUNT_LAYER;
  readonly _CLUSTERS_DEVICES_LAYER = this.mapService._CLUSTERS_DEVICES_LAYER;
  readonly _CLUSTER_MODE = 'circle';

  // FILTERS MANAGEMENT //
  // find a more generic way to implement filters and displayConditions regards to CanvasPointLayer.
  public filters$: Observable<DevicesFilters>;

  private _devicesManagerService = inject(DevicesManagerService);
  private _eventsDeviceUtils = inject(EventsDeviceUtilsService);
  public override properties: LayerSpecification & { source: string; type: LayerSpecification['type'] } = {
    id: this._CLUSTERS_DEVICES_LAYER,
    type: 'symbol',
    source: 'devices-source',
    filter: ['!', ['has', 'point_count']],
    paint: {
      'text-color': '#000000',
      'text-halo-color': '#fff',
      'text-halo-width': 2,
    },
    layout: {
      'icon-image': 'device_{icon}',
      'icon-allow-overlap': true,
      'icon-size': 0.7, // 1
      'text-font': ['Open Sans Regular'],
      'text-field': '{registration}',
      'text-anchor': 'top',
      'text-size': 12,
      'text-offset': [0, 1.5], // [1, 1]
      'text-optional': true,
      'icon-pitch-alignment': 'map',
    },
  };

  constructor() {
    super({
      extraProperties: item =>
        ({
          movingStatus: item.movingStatus.value,
          registration: item.registration,
          driver_name: item.driver_name,
        }) as any,
    });

    this.items = this._devicesManagerService.devices$ as any as BaseLayer<FullDevice>['items']; // TODO : fix dumb type
    this.mapService.eventCreateMap$.subscribe(value => {
      if (this._accountStateService.$isRenault()) {
        if (value) {
          if (this.mapService.map._loaded) {
            this.init();
            this._setup();
          } else
            this.mapService.map.on('load', () => {
              this.init();
              this._setup();
            });
        }
      }
    });
  }

  private _setup() {
    this.mapService.waitForLayer(this.properties.id, () => {
      if (this._accountStateService.$isRenault()) {
        this._setupLayers();

        if (this.mapService.clusterMode === this._CLUSTER_MODE) {
          this.show();
          this.toggleLabel();
        } else this.hide();

        this._setupEvents();

        this.filters$ = this.devicesLayerService.filters$;
        this.filters$.subscribe(value => {
          if (value) this.onFiltersChange(value);
        });
      }
    });
  }

  private onFiltersChange(filters: DevicesFilters) {
    const defaultFilters = { ...(filters || {}) };

    // FILTER CONSTRUCTION
    // TODO: make it more scalable.
    let mapFilters: (string | string[])[] = undefined;

    const unitsFilters = ['!in', 'uid_parent'].concat(defaultFilters.units as any);
    const statusFilters = ['in', 'movingStatus'].concat(defaultFilters.movingStatus);

    if (filters.units?.length || filters.movingStatus?.length) mapFilters = ['all'];
    if (filters.units?.length) mapFilters.push(unitsFilters);
    if (filters.movingStatus?.length) mapFilters.push(statusFilters);

    // SET FILTERS ON MAP
    if (this.mapService.map.getLayer(this.properties.id)) {
      this.mapService.map.setFilter(this.properties.id, mapFilters);
    }

    const style = this.mapService.map.getStyle();
    const sourceStyle = style.sources[this.properties.source] as GeoJSONSourceSpecification;

    if (sourceStyle)
      sourceStyle.cluster = !(defaultFilters.units.length || defaultFilters.movingStatus.length);

    this.mapService.map.setStyle(style);
    if (filters.units.length || filters.movingStatus.length) this.mapService.setClusterMode('inactive');
  }

  public override show() {
    this.mapService.showLayers([this.properties.id, this._CLUSTERS_COUNT_LAYER, this._CLUSTERS_FILLS_LAYER]);
    this.hided = false;

    this._orderLayers();

    this.updateSource();

    return this;
  }

  public override hide() {
    this.mapService.hideLayers([this.properties.id, this._CLUSTERS_COUNT_LAYER, this._CLUSTERS_FILLS_LAYER]);
    this.hided = true;
    return this;
  }

  private _setupLayers() {
    if (!this.mapService.map.getLayer(this._CLUSTERS_FILLS_LAYER)) {
      this.mapService.map.addLayer(
        {
          id: this._CLUSTERS_FILLS_LAYER,
          type: 'circle',
          source: this.properties.source,
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': 'rgba(255, 255, 255, 0.8)',
            'circle-radius': ['step', ['get', 'point_count'], 18, 10, 24, 50, 32, 200, 40],
            'circle-stroke-color': [
              'step',
              ['get', 'point_count'],
              'hsl(186, 100%, 30%)', // low density
              10,
              'hsl(186, 100%, 25%)',
              50,
              'hsl(186, 100%, 20%)',
              200,
              'hsl(186, 100%, 14%)', // high density
            ],
            'circle-stroke-width': 2,
          },
        },
        this.mapService.map.getLayer(this.mapService._LANDMARKS_LAYER)
          ? this.mapService._LANDMARKS_LAYER
          : null
      );
    }

    if (!this.mapService.map.getLayer(this._CLUSTERS_COUNT_LAYER)) {
      this.mapService.map.addLayer(
        {
          id: this._CLUSTERS_COUNT_LAYER,
          type: 'symbol',
          source: this.properties.source,
          filter: ['has', 'point_count'],
          layout: {
            'text-field': ['get', 'point_count_abbreviated'],
            'text-font': this.mapService.fonts,
            'text-size': 12,
          },
          paint: {
            'text-color': [
              'step',
              ['get', 'point_count'],
              'hsl(186, 100%, 30%)', // low density
              10,
              'hsl(186, 100%, 25%)',
              50,
              'hsl(186, 100%, 20%)',
              200,
              'hsl(186, 100%, 14%)', // high density
            ],
          },
        },
        this.mapService.map.getLayer(this.mapService._LANDMARKS_LAYER)
          ? this.mapService._LANDMARKS_LAYER
          : null
      );
    }
  }

  private _orderLayers() {
    if (this.mapService.map.getLayer(this.mapService._LANDMARKS_LAYER)) {
      if (this.mapService.map.getLayer(this._CLUSTERS_DEVICES_LAYER)) {
        this.mapService.map.moveLayer(this._CLUSTERS_DEVICES_LAYER);
      }
      if (this.mapService.map.getLayer(this._CLUSTERS_FILLS_LAYER)) {
        this.mapService.map.moveLayer(this._CLUSTERS_FILLS_LAYER);
      }
      if (this.mapService.map.getLayer(this._CLUSTERS_COUNT_LAYER)) {
        this.mapService.map.moveLayer(this._CLUSTERS_COUNT_LAYER);
      }
    }
  }

  private _setupEvents() {
    this.mapService.map.addInteraction('click-clusters', {
      type: 'click',
      target: { layerId: this._CLUSTERS_FILLS_LAYER },
      handler: e => {
        const features = this.mapService.map.queryRenderedFeatures(e.point, {
          layers: [this._CLUSTERS_FILLS_LAYER],
        });
        const clusterId = features[0].properties.cluster_id;
        const source = this.mapService.map.getSource(this.properties.source);

        source
          //@ts-ignore getClusterExpansionZoom exist into source
          .getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return;

            this.mapService.map.easeTo({
              //@ts-ignore coordinates exist int geometry
              center: features[0].geometry.coordinates,
              zoom: zoom,
            });
          });
      },
    });

    this.onClick(event => {
      const feature = event.features[0];
      this._eventsDeviceUtils.displayDevice(feature);
    });

    this.onMouseEnter(event => {
      const feature = event.features[0];
      this._eventsDeviceUtils.displayPopup(feature);
    });

    this.onMouseLeave(() => {
      this._eventsDeviceUtils.removePopup();
    });

    this.mapService.map.on('moveend', () => {
      this.$visiblePoints.set(this.getCurrentVisiblePoints());
    });
  }

  // TODO: LEGACY METHOD
  public toggleLabel(preference?: string) {
    this._eventsDeviceUtils.toggleLabel(this.properties.id, preference);
  }
}
